import React, { useEffect, useState } from 'react';
import { X, Clock, CheckCircle, XCircle, Undo2, FileSignature, RotateCcw, Send } from 'lucide-react';
import { getReviewHistory } from '../../services/reviewWorkflowApi';

/**
 * Read-only attestation history for one reviewable entity (FECOA-1611).
 *
 * Each ReviewSubmission is a submit-to-decision cycle. We render it as two
 * fixed timeline nodes — a "Submitted" node (always) and a "Decision" node
 * (the outcome, or an "Awaiting decision" placeholder while it is still
 * pending) — so the timeline reads consistently regardless of state instead
 * of a row that grows a second line only once decided. Includes the decision
 * reason and the 21 CFR Part 11 e-signature block (meaning / signed-by / at).
 */

// The submission half of a cycle — always present.
const SUBMIT_META = {
  label: 'Submitted for review',
  icon: Send,
  cls: 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-900/30 dark:border-blue-800',
};

// The decision half of a cycle, keyed by the submission's terminal status.
const DECISION_META = {
  approved: { label: 'Approved', icon: CheckCircle, cls: 'text-teal-700 bg-teal-50 border-teal-200 dark:text-teal-300 dark:bg-teal-900/30 dark:border-teal-800' },
  denied: { label: 'Denied', icon: XCircle, cls: 'text-red-600 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-900/30 dark:border-red-800' },
  withdrawn: { label: 'Withdrawn', icon: Undo2, cls: 'text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700' },
  revoked: { label: 'Approval revoked', icon: RotateCcw, cls: 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-900/30 dark:border-amber-800' },
};

// Placeholder decision node shown while a submission is still pending, so the
// cycle always has two nodes and doesn't look truncated.
const AWAITING_META = {
  label: 'Awaiting decision',
  icon: Clock,
  cls: 'text-gray-400 bg-white border-gray-300 border-dashed dark:text-gray-500 dark:bg-gray-800 dark:border-gray-600',
};

// Header chip mirroring the entity's card badge, so the modal's story matches
// what the user clicked from. Keyed by the entity's current status.
const CURRENT_STATUS_META = {
  draft: { label: 'Draft', cls: 'text-gray-600 bg-gray-100 border-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600' },
  in_review: { label: 'In review', cls: 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-900/30 dark:border-blue-800' },
  pending: { label: 'In review', cls: 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-900/30 dark:border-blue-800' },
  approved: { label: 'Approved', cls: 'text-teal-700 bg-teal-50 border-teal-200 dark:text-teal-300 dark:bg-teal-900/30 dark:border-teal-800' },
  published: { label: 'Published', cls: 'text-teal-700 bg-teal-50 border-teal-200 dark:text-teal-300 dark:bg-teal-900/30 dark:border-teal-800' },
  denied: { label: 'Changes requested', cls: 'text-red-600 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-900/30 dark:border-red-800' },
  archived: { label: 'Archived', cls: 'text-gray-500 bg-gray-100 border-gray-200 dark:text-gray-400 dark:bg-gray-700 dark:border-gray-600' },
};

const fmt = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const ts = (value) => {
  const t = value ? new Date(value).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
};

// Plain-English explanation of the entity_version hash, shown on hover.
const VERSION_TOOLTIP =
  'Unique system identifier for the exact content that was reviewed. '
  + 'If you ever need to trace this approval, or flag it as made in error, '
  + 'share this ID with support and they can pull up the full record.';

// One circular node sitting on the timeline rail, with its badge + body.
const TimelineNode = ({ meta, badgeExtra, topMargin, children }) => {
  const Icon = meta.icon;
  return (
    <li className={`ml-5 ${topMargin}`}>
      <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full border ${meta.cls}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${meta.cls}`}>
          {meta.label}
        </span>
        {badgeExtra}
      </div>
      {children}
    </li>
  );
};

const ReviewHistory = ({ entityType = 'assessment', objectId, entityName, currentStatus, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getReviewHistory(entityType, objectId);
        if (active) setRows(Array.isArray(data) ? data : data.results || []);
      } catch {
        if (active) setError('Could not load review history.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [entityType, objectId]);

  // Oldest cycle first so the rail reads top-to-bottom in the order things
  // happened (submitted, then decided, then any resubmission).
  const cycles = [...rows].sort((a, b) => ts(a.submitted_at) - ts(b.submitted_at));
  const statusMeta = currentStatus ? CURRENT_STATUS_META[currentStatus] : null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-[10000] p-4 text-left whitespace-normal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Review history</h3>
            {entityName && <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{entityName}</p>}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {statusMeta && (
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${statusMeta.cls}`}>
                {statusMeta.label}
              </span>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto">
          {loading && <div className="text-center py-10 text-gray-400 text-sm">Loading history…</div>}
          {error && <div className="text-sm text-red-600 py-4">{error}</div>}
          {!loading && !error && cycles.length === 0 && (
            <div className="text-center py-10 text-gray-500 text-sm">
              No review activity yet for this item.
            </div>
          )}

          {!loading && cycles.length > 0 && (
            <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-2">
              {cycles.map((r, ci) => {
                const decided = r.is_decided;
                const decisionMeta = decided ? (DECISION_META[r.status] || DECISION_META.approved) : AWAITING_META;
                // A withdrawal is the submitter recalling their own submission,
                // not an approver decision — fall back to the submitter for the
                // decider fields, and prefer the frozen signature name so the
                // record stays consistent even if the approver later renames.
                const deciderName = r.signed_by_name
                  || r.decided_by_name
                  || (r.status === 'withdrawn' ? r.submitted_by_name : null)
                  || 'Unknown';
                const deciderEmail = r.status === 'withdrawn' ? r.submitted_by_email : r.signed_by_email;

                const versionChip = r.entity_version ? (
                  <span className="inline-flex items-center gap-1 cursor-help" title={VERSION_TOOLTIP}>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">Reviewed version</span>
                    <span className="text-[11px] font-mono text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full px-1.5 py-0.5">
                      {String(r.entity_version).slice(0, 8)}
                    </span>
                  </span>
                ) : null;

                return (
                  <React.Fragment key={r.id}>
                    {/* Submitted node — always present */}
                    <TimelineNode
                      meta={SUBMIT_META}
                      badgeExtra={versionChip}
                      topMargin={ci === 0 ? 'mt-1' : 'mt-7'}
                    >
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        by <span className="font-medium">{r.submitted_by_name || 'Unknown'}</span>
                        {r.submitted_by_email && r.submitted_by_email !== r.submitted_by_name && (
                          <span className="text-gray-400"> ({r.submitted_by_email})</span>
                        )}
                        <span className="text-gray-400"> · {fmt(r.submitted_at)}</span>
                      </p>
                    </TimelineNode>

                    {/* Decision node — the outcome, or an awaiting placeholder */}
                    <TimelineNode meta={decisionMeta} topMargin="mt-4">
                      {decided ? (
                        <>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            by <span className="font-medium">{deciderName}</span>
                            {deciderEmail && deciderEmail !== deciderName && (
                              <span className="text-gray-400"> ({deciderEmail})</span>
                            )}
                            <span className="text-gray-400"> · {fmt(r.decided_at)}</span>
                          </p>
                          {r.decision_reason && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded-md px-3 py-2">
                              “{r.decision_reason}”
                            </p>
                          )}
                          {r.signed_by_name && (
                            <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                              <FileSignature className="w-3.5 h-3.5" />
                              {r.signature_meaning || 'Signed'}, {r.signed_by_name}
                              {r.signed_by_email && r.signed_by_email !== r.signed_by_name && (
                                <span className="text-gray-400"> ({r.signed_by_email})</span>
                              )}
                              <span className="text-gray-400"> · {fmt(r.signed_at)}</span>
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-gray-400 dark:text-gray-500">
                          Waiting for an approver to review this submission.
                        </p>
                      )}
                    </TimelineNode>
                  </React.Fragment>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewHistory;
